import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './CommentDrawer.css';
import { Link } from 'react-router-dom';
import placeholderProfile from '../../assets/placeholder-profile.png';
import ReadMoreText from '../../common/ReadMoreText';
import CommentDrawerSkeleton from '../skeleton/CommentDrawerSkeleton';
import DeleteCommentDrawer from './DeleteCommentDrawer';
import BlockConfirmDrawer from './BlockConfirmDrawer';
import api from '../../api';

import goldTickIcon from '../../assets/goldtick-icon.png';
import sendIcon from '../../assets/send-icon.png';

// 🖼️ Namespaced comment reaction assets (avoid conflicts with other like icons)
// 👍 use thumbs up for comment likes
import commentLikeIcon from '../../assets/thumbsup-icon.png';
import commentLikeActiveIcon from '../../assets/thumbsupfilled-icon.png';
import commentDislikeIcon from '../../assets/dislike-icon.png';
import commentDislikeActiveIcon from '../../assets/dislikefilled-icon.png';

const QUICK_COMMENT_EMOJIS = ['❤️', '🙌', '🔥', '👏', '😂', '😍', '😭'];

/**
 * Props:
 * - onClose: () => void
 * - comments: array (optional seed; we now prefer server interactions)
 * - newComment, setNewComment
 * - handleComment: () => void | Promise<any>  // may return created comment
 * - replyingTo, setReplyingTo
 * - postId?: string
 * - currentUser?: string
 * - postOwnerUsername?: string
 * - onAfterDelete?: (updatedComments) => void
 * - onCommentDeleted?: (deletedId: string) => void
 */

// 🔧 Keep a locked prefix for replies like "@sizwe "
const withPrefix = (value, prefix) => {
  if (!prefix) return value;
  const lc = (value || '').toLowerCase();
  const plc = prefix.toLowerCase();
  if (lc.startsWith(plc)) return prefix + value.slice(prefix.length);
  return prefix + String(value || '').replace(/^@\w+\s+/, '');
};

// ⏱️ Same time-ago logic as in Post.js
const getTimeAgo = (ts) => {
  if (!ts) return '';
  let t = ts;
  if (typeof t === 'number' && String(t).length === 10) t = t * 1000;
  const dt = new Date(t);
  if (isNaN(dt.getTime())) return '';

  const now = new Date();
  let diffMs = now.getTime() - dt.getTime();
  if (diffMs < 0) diffMs = 0;

  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'Just now';

  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;

  const day = Math.floor(hr / 24);
  if (day <= 7) return `${day}d ago`;

  if (day < 365) {
    const d = dt.getDate();
    const mon = dt.toLocaleString('en-US', { month: 'short' }).toLowerCase();
    return `${d} ${mon}`;
  }
  const years = Math.floor(day / 365);
  return years === 1 ? '1 year ago' : `${years} years ago`;
};

const isAdminUser = (u) => u?.isAdmin === true || !!u?.adminRole;
const isAppUser = (u) => u?.isApp === true || u?.accountType === 'app';
const isSpecialUser = (u) => isAdminUser(u) || isAppUser(u);

const getSpecialTitle = (u) => {
  if (isAdminUser(u)) return u?.adminRole || 'Admin';
  if (isAppUser(u)) return 'App account';
  return '';
};

// Count all descendants (replies + replies of replies)
const countRepliesDeep = (list) => {
  if (!Array.isArray(list)) return 0;
  let total = 0;
  for (const r of list) {
    total += 1;
    if (Array.isArray(r.replies) && r.replies.length) {
      total += countRepliesDeep(r.replies);
    }
  }
  return total;
};



function ReplyThread({
  reply,
  parentCommentId,
  postId,
  currentUser,
  replyingTo,
  setReplyingTo,
  startReply,
  reactToReply,
  reacting,
  startLongPressReply,
  cancelLongPress,
  commentBlocked = false,
  checkingPrivacy = false,
}) {

  const isReacting = !!reacting[reply.id];


const isReplyTarget =
  Boolean(replyingTo?.replyId) &&
  String(replyingTo.replyId) ===
    String(reply.id);

  const likeActive = reply.userReaction === 'like';
  const dislikeActive = reply.userReaction === 'dislike';

  return (
    <div className="reply-thread">

<div
  className={`reply-entry ${
    isReplyTarget
      ? 'reply-entry--reply-target'
      : ''
  }`}
  data-comment-drawer-target-id={reply.id}
  onMouseDown={startLongPressReply({ id: parentCommentId }, reply)}

        onMouseUp={cancelLongPress}
        onMouseLeave={cancelLongPress}
        onTouchStart={startLongPressReply({ id: parentCommentId }, reply)}
        onTouchEnd={cancelLongPress}
      >
        <img src={reply.profilePic || placeholderProfile} alt="Reply Avatar" className="reply-avatar" />

        <div className="reply-bubble">
          <div className="comment-meta">
            <Link to={`/user/${reply.username}`} className="comment-link">
              <span className="comment-name-row">
                <span>@{reply.username}</span>
                {isSpecialUser(reply) && (
                  <img
                    src={goldTickIcon}
                    alt={isAdminUser(reply) ? 'Admin verified' : 'App verified'}
                    className="comment-gold-tick"
                    title={getSpecialTitle(reply)}
                  />
                )}
              </span>
            </Link>
            <span className="dot-sep" aria-hidden="true">•</span>
            <span className="timestamp-inline">
              {getTimeAgo(reply.createdAt || reply.timestamp)}
            </span>
          </div>

          <p className="comment-text">
            <ReadMoreText text={reply.text} maxLines={3} inline />
          </p>

          <div className="comment-actions-row">


<button
  className="reply-btn"
  disabled={commentBlocked || checkingPrivacy}
  onClick={() =>
    startReply({
      commentId: parentCommentId,
      replyId: reply.id,
      username: reply.username,
    })
  }
>
  Reply
</button>


            <div className="comment-actions-right">
              <button
                className={`comment-action-btn ${likeActive ? 'is-active' : ''}`}
                disabled={isReacting || !currentUser || !postId}
                onClick={() => reactToReply(parentCommentId, reply.id, 'like')}
              >
                <img src={likeActive ? commentLikeActiveIcon : commentLikeIcon} alt="like" className="comment-action-icon" />
              </button>
              <span className="comment-action-count">{reply.likesCount ?? 0}</span>

              <button
                className={`comment-action-btn ${dislikeActive ? 'is-active' : ''}`}
                disabled={isReacting || !currentUser || !postId}
                onClick={() => reactToReply(parentCommentId, reply.id, 'dislike')}
              >
                <img src={dislikeActive ? commentDislikeActiveIcon : commentDislikeIcon} alt="dislike" className="comment-action-icon" />
              </button>
              <span className="comment-action-count">{reply.dislikesCount ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {reply.replies?.length > 0 && (
        <div className="reply-children">
          {reply.replies.map((child) => (


<ReplyThread
  key={child.id}
  reply={child}
  parentCommentId={parentCommentId}
  postId={postId}
  currentUser={currentUser}
  replyingTo={replyingTo}
  setReplyingTo={setReplyingTo}
  startReply={startReply}
  reactToReply={reactToReply}
  reacting={reacting}
  startLongPressReply={startLongPressReply}
  cancelLongPress={cancelLongPress}
  commentBlocked={commentBlocked}
  checkingPrivacy={checkingPrivacy}
/>


          ))}
        </div>
      )}
    </div>
  );
}

export default function CommentDrawer({
  onClose,
  comments = [],
  newComment,
  setNewComment,
  handleComment,
  replyingTo,
  setReplyingTo,

postId,
currentUser,
currentUserProfilePic = '',
postOwnerUsername,
onAfterDelete,
onCommentDeleted,

}) {
  const [enrichedComments, setEnrichedComments] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

const [posting, setPosting] = useState(false);

// 🔒 Privacy state: disables comment/reply when blocked
const [commentBlocked, setCommentBlocked] = useState(false);
const [checkingPrivacy, setCheckingPrivacy] = useState(false);


  const [reacting, setReacting] = useState({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

const [blockOpen, setBlockOpen] = useState(false);
const [blockTarget, setBlockTarget] = useState(null);
const [blockActionLoading, setBlockActionLoading] = useState(false);

// Tracks block/unblock state inside this open comment drawer session
const [blockedUsernames, setBlockedUsernames] = useState(new Set());




// Small success/error popup
const [commentToast, setCommentToast] = useState({
  visible: false,
  message: '',
  type: '',
});

const showCommentToast = (message, type = 'success', duration = 2200) => {
  setCommentToast({ visible: true, message, type });

  window.clearTimeout(showCommentToast._t);
  showCommentToast._t = window.setTimeout(() => {
    setCommentToast({ visible: false, message: '', type: '' });
  }, duration);
};

const isUsernameBlockedLocally = (targetUsername) => {
  return blockedUsernames.has(String(targetUsername || '').toLowerCase());
};


const syncBlockStatusForUser = async (targetUsername) => {
  if (!targetUsername || !currentUser) return false;

  try {
    const res = await api.get(`/privacy/status/${targetUsername}`);

    const iBlocked = res.data?.iBlockedThem === true;
    const targetKey = String(targetUsername).toLowerCase();

    setBlockedUsernames((prev) => {
      const next = new Set(prev);

      if (iBlocked) {
        next.add(targetKey);
      } else {
        next.delete(targetKey);
      }

      return next;
    });

    return iBlocked;
  } catch (err) {
    console.error('❌ Failed to sync block status for comment user:', err);
    return isUsernameBlockedLocally(targetUsername);
  }
};


  const [total, setTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState(0);
  const [exhausted, setExhausted] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState('');

const scrollRef = useRef(null);
const bottomSentinelRef = useRef(null);
const commentTextareaRef = useRef(null);

const startReply =
  useCallback((target) => {
    if (!target) {
      return;
    }

    setReplyingTo(target);
  }, [setReplyingTo]);

const keepReplyTargetVisible =
  useCallback(() => {
    if (!replyingTo?.commentId) {
      return;
    }

    const targetId =
      replyingTo?.replyId ||
      replyingTo?.commentId;

    const scrollArea =
      scrollRef.current;

    if (!targetId || !scrollArea) {
      return;
    }

    const target =
      Array.from(
        scrollArea.querySelectorAll(
          '[data-comment-drawer-target-id]'
        )
      ).find(
        (element) =>
          String(
            element.dataset
              .commentDrawerTargetId
          ) === String(targetId)
      );

    if (!target) {
      return;
    }

    const targetRect =
      target.getBoundingClientRect();

    const scrollRect =
      scrollArea.getBoundingClientRect();

    const viewportBottom =
      window.visualViewport
        ? window.visualViewport.offsetTop +
          window.visualViewport.height
        : window.innerHeight;

    const safeTop =
      scrollRect.top + 12;

    const safeBottom =
      Math.min(
        scrollRect.bottom,
        viewportBottom
      ) - 16;

    if (
      targetRect.top >= safeTop &&
      targetRect.bottom <= safeBottom
    ) {
      return;
    }

    const availableHeight =
      Math.max(
        0,
        safeBottom - safeTop
      );

    let desiredTop =
      safeTop;

    if (
      targetRect.height <
      availableHeight
    ) {
      desiredTop =
        safeTop +
        Math.max(
          0,
          (
            availableHeight -
            targetRect.height
          ) / 2
        );
    }

    const scrollDifference =
      targetRect.top -
      desiredTop;

    scrollArea.scrollBy({
      top: scrollDifference,
      behavior: 'smooth',
    });
  }, [replyingTo]);


useEffect(() => {
  if (!replyingTo?.commentId) {
    return undefined;
  }

  const repositionTarget = () => {
    keepReplyTargetVisible();
  };

  const firstFrame =
    requestAnimationFrame(
      repositionTarget
    );

  const shortDelay =
    window.setTimeout(
      repositionTarget,
      120
    );

  const keyboardDelay =
    window.setTimeout(
      repositionTarget,
      320
    );

  const visualViewport =
    window.visualViewport;

  visualViewport?.addEventListener(
    'resize',
    repositionTarget
  );

  visualViewport?.addEventListener(
    'scroll',
    repositionTarget
  );

  return () => {
    cancelAnimationFrame(
      firstFrame
    );

    window.clearTimeout(
      shortDelay
    );

    window.clearTimeout(
      keyboardDelay
    );

    visualViewport?.removeEventListener(
      'resize',
      repositionTarget
    );

    visualViewport?.removeEventListener(
      'scroll',
      repositionTarget
    );
  };
}, [
  replyingTo,
  keepReplyTargetVisible,
]);


  const commentRefs = useRef(new Map());
  const loadMoreRef = useRef(null);

  const pushedRef = useRef(false);
  const closingFromButtonRef = useRef(false);


const onCloseRef = useRef(onClose);

  const deletedIdsRef = useRef(new Set());
  const optimisticRef = useRef(false);
  const snapshotRef = useRef(null);

  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (commentId) =>
    setExpanded((prev) => ({ ...prev, [commentId]: !prev[commentId] }));

  const pendingScrollRef = useRef(null);

useEffect(() => {
  onCloseRef.current = onClose;
}, [onClose]);

useEffect(() => {
  if (!currentUser || !postOwnerUsername) {
    setCommentBlocked(false);
    setCheckingPrivacy(false);
    return;
  }

  // Post owner can always comment/reply on their own post
  if (
    String(currentUser).toLowerCase() ===
    String(postOwnerUsername).toLowerCase()
  ) {
    setCommentBlocked(false);
    setCheckingPrivacy(false);
    return;
  }

  let cancelled = false;

  const checkPrivacyStatus = async () => {
    try {
      setCheckingPrivacy(true);

      const res = await api.get(`/privacy/status/${postOwnerUsername}`);

      if (cancelled) return;

      const blocked =
        res.data?.canComment === false ||
        res.data?.theyBlockedMe === true ||
        res.data?.iBlockedThem === true;

      setCommentBlocked(Boolean(blocked));
    } catch (err) {
      console.error('❌ Failed to check comment privacy:', err);

      if (!cancelled) {
        // Fail open so comments do not break if privacy endpoint has an issue.
        // Backend still protects blocked actions.
        setCommentBlocked(false);
      }
    } finally {
      if (!cancelled) {
        setCheckingPrivacy(false);
      }
    }
  };

  checkPrivacyStatus();

  return () => {
    cancelled = true;
  };
}, [currentUser, postOwnerUsername]);


  const normalizeOne = (c) => ({
    ...c,
    id: c?.id || c?._id || c?.commentId || null,
    createdAt: c?.createdAt || c?.timestamp || c?.date || null,
    username:
      c?.username ||
      c?.user?.username ||
      c?.author?.username ||
      c?.postedBy ||
      null,
    likesCount: typeof c?.likesCount === 'number' ? c.likesCount : (Array.isArray(c?.likes) ? c.likes.length : 0),
    dislikesCount: typeof c?.dislikesCount === 'number' ? c.dislikesCount : (Array.isArray(c?.dislikes) ? c.dislikes.length : 0),
    userReaction: c?.userReaction ?? null,
    profilePic: (typeof c?.profilePic === 'string' && c.profilePic.startsWith('http')) ? c.profilePic : placeholderProfile,
    replies: (c?.replies || []).map((r) => ({
      ...r,
      id: r?.id || r?._id || r?.replyId || null,
      createdAt: r?.createdAt || r?.timestamp || r?.date || null,
      username: r?.username || r?.user?.username || r?.author?.username || r?.postedBy || null,
      likesCount: typeof r?.likesCount === 'number' ? r.likesCount : (Array.isArray(r?.likes) ? r.likes.length : 0),
      dislikesCount: typeof r?.dislikesCount === 'number' ? r.dislikesCount : (Array.isArray(r?.dislikes) ? r.dislikes.length : 0),
      userReaction: r?.userReaction ?? null,
      profilePic: (typeof r?.profilePic === 'string' && r.profilePic.startsWith('http')) ? r.profilePic : placeholderProfile,
    })),
  });

  const normalize = (list) =>
    (Array.isArray(list) ? list.map(normalizeOne) : []).filter(
      (c) => !!c.id && !deletedIdsRef.current.has(c.id)
    );

  const setFromRaw = (rawList) => {
    const norm = normalize(rawList);
    setEnrichedComments(norm);

    setExpanded((prev) => {
      const next = { ...prev };
      norm.forEach((c) => {
        if (typeof next[c.id] === 'undefined') next[c.id] = false;
      });
      return next;
    });
  };

  const fetchPage = useCallback(
    async (offset = 0) => {
      const params = {
        limit: 20,
        offset,
      };
      if (currentUser) params.viewer = currentUser;

      const { data } = await api.get(`/interactions/${postId}/comments`, { params });
      return data;
    },
    [postId, currentUser]
  );

  const refreshInteractions = async () => {
    if (!postId) return;
    try {
      setLoadError('');
      setRefreshing(!initialLoad);
      const data = await fetchPage(0);

      setFromRaw(data?.items || []);
      setTotal(data?.total || 0);

      if (data?.nextOffset == null) {
        setExhausted(true);
        setNextOffset(0);
      } else {
        setExhausted(false);
        setNextOffset(data.nextOffset);
      }
    } catch (err) {
      console.error('❌ Failed to load comments (page 0):', err);
      setLoadError('Failed to load comments.');
      setFromRaw(comments);
    } finally {
      setInitialLoad(false);
      setRefreshing(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (loadingMore || exhausted) return;
    try {
      setLoadingMore(true);
      const data = await fetchPage(nextOffset);
      const nextItems = data?.items || [];

      setFromRaw([...enrichedComments, ...nextItems]);
      if (data?.nextOffset == null) {
        setExhausted(true);
      } else {
        setNextOffset(data.nextOffset);
      }
    } catch (err) {
      console.error('❌ Failed to load more comments:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, nextOffset, loadingMore, exhausted, enrichedComments]);



const safeClose = useCallback(({ manual }) => {
  if (manual) closingFromButtonRef.current = true;
  try { onCloseRef.current?.(); } catch {}

  if (manual && pushedRef.current && window.history.state?.bfDrawer === 'comments') {
    try { window.history.back(); } catch {}
    pushedRef.current = false;
  }
}, []);


  const handleBackdropClick = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    safeClose({ manual: true });
  }, [safeClose]);

  const stopDrawerPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);




useEffect(() => {
  try {
    window.history.pushState({ bfDrawer: 'comments' }, '');
    pushedRef.current = true;
  } catch {}

  const body = document.body;
  const prevOverflow = body.style.overflow;

  body.style.overflow = 'hidden';
  body.classList.add('bf-comment-drawer-open');

  const onPopState = () => {
    if (closingFromButtonRef.current) {
      closingFromButtonRef.current = false;
      return;
    }

    safeClose({ manual: false });
  };

  window.addEventListener('popstate', onPopState);

  return () => {
    body.style.overflow = prevOverflow;
    body.classList.remove('bf-comment-drawer-open');

    window.removeEventListener('popstate', onPopState);

    if (pushedRef.current && window.history.state?.bfDrawer === 'comments') {
      try { window.history.back(); } catch {}
      pushedRef.current = false;
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);



  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || initialLoad || refreshing || exhausted) return;

    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    }, { rootMargin: '200px' });

    io.observe(el);
    return () => io.disconnect();
  }, [loadMore, initialLoad, refreshing, exhausted]);

  useEffect(() => {
    refreshInteractions();
  }, [postId, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const target = pendingScrollRef.current;
    if (!target) return;

    if (target.type === 'comment' && target.commentId) {
      setExpanded((prev) => ({ ...prev, [target.commentId]: true }));
    }

    requestAnimationFrame(() => {
      if (target.type === 'bottom') {
        bottomSentinelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else if (target.type === 'top') {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (target.type === 'comment' && target.commentId) {
        const el = commentRefs.current.get(target.commentId);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      pendingScrollRef.current = null;
    });
  }, [enrichedComments]);

  const lpTimerRef = useRef(null);
  const clearLP = () => {
    if (lpTimerRef.current) {
      clearTimeout(lpTimerRef.current);
      lpTimerRef.current = null;
    }
  };


const isCurrentUserPostOwner = () =>
  !!currentUser &&
  !!postOwnerUsername &&
  currentUser.toLowerCase() === postOwnerUsername.toLowerCase();

const isSameUser = (a, b) =>
  String(a || '').toLowerCase() === String(b || '').toLowerCase();

const canDeleteComment = (c) =>
  !!currentUser &&
  (isSameUser(currentUser, c?.username) || isCurrentUserPostOwner());

const canDeleteReply = (r) =>
  !!currentUser &&
  (isSameUser(currentUser, r?.username) || isCurrentUserPostOwner());

const canBlockCommentAuthor = (c) =>
  !!currentUser &&
  isCurrentUserPostOwner() &&
  !!c?.username &&
  !isSameUser(currentUser, c.username);

const canBlockReplyAuthor = (r) =>
  !!currentUser &&
  isCurrentUserPostOwner() &&
  !!r?.username &&
  !isSameUser(currentUser, r.username);


const startLongPressComment = (c) => () => {
  if (!postId || !c?.id) return;

  clearLP();

  lpTimerRef.current = setTimeout(async () => {
    // ✅ Post owner long-pressing another user's comment = block/unblock flow
    if (canBlockCommentAuthor(c)) {
      const targetUsername = c.username;

      setBlockTarget({
        type: 'comment',
        targetUsername,
        textSnippet: c.text || '',
      });

      await syncBlockStatusForUser(targetUsername);

      setBlockOpen(true);
      return;
    }

    // ✅ Comment owner / post owner delete flow stays available
    if (canDeleteComment(c)) {
      setDeleteTarget({
        type: 'comment',
        commentId: c.id,
        authorUsername: c.username,
        textSnippet: c.text || '',
        createdAt: c.createdAt || '',
      });
      setDeleteOpen(true);
    }
  }, 500);
};


const startLongPressReply = (c, r) => () => {
  if (!postId || !c?.id || !r?.id) return;

  clearLP();

  lpTimerRef.current = setTimeout(async () => {
    // ✅ Post owner long-pressing another user's reply = block/unblock flow
    if (canBlockReplyAuthor(r)) {
      const targetUsername = r.username;

      setBlockTarget({
        type: 'reply',
        targetUsername,
        textSnippet: r.text || '',
      });

      await syncBlockStatusForUser(targetUsername);

      setBlockOpen(true);
      return;
    }

    // ✅ Reply owner / post owner delete flow stays available
    if (canDeleteReply(r)) {
      setDeleteTarget({
        type: 'reply',
        commentId: c.id,
        replyId: r.id,
        authorUsername: r.username,
        textSnippet: r.text || '',
        createdAt: r.createdAt || '',
        replyMeta: { timestamp: r.createdAt || r.timestamp, text: r.text },
      });
      setDeleteOpen(true);
    }
  }, 500);
};

const doToggleBlockUser = async () => {
  if (!blockTarget?.targetUsername || !currentUser) return;

  const targetUsername = blockTarget.targetUsername;
  const targetKey = String(targetUsername).toLowerCase();

  const currentlyBlocked = isUsernameBlockedLocally(targetUsername);
  const nextAction = currentlyBlocked ? 'unblock' : 'block';

  try {
    setBlockActionLoading(true);

    await api.post(`/privacy/${nextAction}`, {
      targetUsername,
    });

    setBlockedUsernames((prev) => {
      const next = new Set(prev);

      if (nextAction === 'block') {
        next.add(targetKey);
      } else {
        next.delete(targetKey);
      }

      return next;
    });

    setBlockOpen(false);
    setBlockTarget(null);

    showCommentToast(
      nextAction === 'block'
        ? `@${targetUsername} has been blocked.`
        : `@${targetUsername} has been unblocked.`,
      'success'
    );
  } catch (err) {
    console.error(`❌ Failed to ${nextAction} user from comment drawer:`, err);

    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      `Failed to ${nextAction} user. Please try again.`;

    showCommentToast(msg, 'error');
  } finally {
    setBlockActionLoading(false);
  }
};

  const cancelLongPress = () => clearLP();

  const doDelete = async () => {
    if (!deleteTarget || !postId || !currentUser) return;

    const { type, commentId, replyId, replyMeta, textSnippet, createdAt } = deleteTarget;
    if (type === 'comment' && !commentId) return;

    try {
      if (type === 'comment') {
        try {
          await api.delete(`/posts/${postId}/comments/${commentId}`, {
            data: { username: currentUser },
          });
        } catch (err) {
          const status = err?.response?.status;
          if (status === 404) {
            await api.delete(`/posts/${postId}/comments/legacy`, {
              data: {
                username: currentUser,
                commentTimestamp: createdAt || null,
                commentText: textSnippet || null,
              },
            });
          } else {
            throw err;
          }
        }

        if (optimisticRef.current) {
          onCommentDeleted?.(commentId);
          optimisticRef.current = false;
          return;
        }

        deletedIdsRef.current.add(commentId);
        setEnrichedComments((prev) => {
          const next = prev.filter((c) => c.id !== commentId);
          onAfterDelete?.(next);
          onCommentDeleted?.(commentId);
          return next;
        });
      } else {
        const payload = { username: currentUser };
        let url = `/posts/${postId}/comments/${commentId}/replies/${replyId || ''}`;
        try {
          if (!replyId) {
            if (replyMeta?.timestamp) payload.replyTimestamp = replyMeta.timestamp;
            if (replyMeta?.text) payload.replyText = replyMeta.text;
            url = `/posts/${postId}/comments/${commentId}/replies/legacy`;
          }
          await api.delete(url, { data: payload });
        } catch (err) {
          throw err;
        }

        if (optimisticRef.current) {
          optimisticRef.current = false;
          return;
        }

        setEnrichedComments((prev) => {
          const next = prev.map((c) => {
            if (c.id !== commentId) return c;
            const filtered = (c.replies || []).filter((r) => {
              if (replyId) return r.id !== replyId;
              const tsMatch = replyMeta?.timestamp
                ? (r.createdAt || r.timestamp) === replyMeta.timestamp
                : true;
              const textMatch = replyMeta?.text ? r.text === replyMeta.text : true;
              return !(tsMatch && textMatch);
            });
            return { ...c, replies: filtered };
          });
          onAfterDelete?.(next);
          return next;
        });
      }
    } catch (err) {
      console.error('❌ Failed to delete:', err);
      if (optimisticRef.current && snapshotRef.current) {
        setEnrichedComments(snapshotRef.current);
        optimisticRef.current = false;
      }
      throw err;
    }
  };

  const setReactingFor = (id, val) =>
    setReacting((prev) => ({ ...prev, [id]: val }));

  const applyOptimisticReaction = (commentId, action) => {
    setEnrichedComments((prev) =>
      prev.map((c) => {
        if (c.id !== commentId) return c;

        let likesCount = c.likesCount ?? 0;
        let dislikesCount = c.dislikesCount ?? 0;
        let userReaction = c.userReaction ?? null;

        const isLike = action === 'like';
        const isDislike = action === 'dislike';

        if (isLike) {
          if (userReaction === 'like') {
            likesCount = Math.max(0, likesCount - 1);
            userReaction = null;
          } else {
            likesCount = likesCount + 1;
            if (userReaction === 'dislike') {
              dislikesCount = Math.max(0, dislikesCount - 1);
            }
            userReaction = 'like';
          }
        } else if (isDislike) {
          if (userReaction === 'dislike') {
            dislikesCount = Math.max(0, dislikesCount - 1);
            userReaction = null;
          } else {
            dislikesCount = dislikesCount + 1;
            if (userReaction === 'like') {
              likesCount = Math.max(0, likesCount - 1);
            }
            userReaction = 'dislike';
          }
        }

        return { ...c, likesCount, dislikesCount, userReaction };
      })
    );
  };

  const mergeServerReaction = (serverComment) => {
    setEnrichedComments((prev) =>
      prev.map((c) =>
        c.id === serverComment.id
          ? {
              ...c,
              likesCount: serverComment.likesCount ?? c.likesCount ?? 0,
              dislikesCount: serverComment.dislikesCount ?? c.dislikesCount ?? 0,
              userReaction: serverComment.userReaction ?? c.userReaction ?? null,
              replies: Array.isArray(serverComment.replies) ? serverComment.replies : c.replies,
            }
          : c
      )
    );
  };

  const reactToComment = async (commentId, desiredAction) => {
    if (!postId || !currentUser || reacting[commentId]) return;
    try {
      setReactingFor(commentId, true);
      applyOptimisticReaction(commentId, desiredAction);

      const res = await api.post('/posts/comments/react', {
        postId,
        commentId,
        username: currentUser,
        action: desiredAction,
      });

      if (res?.data?.comment) {
        mergeServerReaction(res.data.comment);
      } else {
        await refreshInteractions();
      }
    } catch (err) {
      console.error('❌ reactToComment failed:', err);
      await refreshInteractions();
    } finally {
      setReactingFor(commentId, false);
    }
  };

  const reactToReply = async (commentId, replyId, desiredAction) => {
    if (!postId || !currentUser || reacting[replyId]) return;
    try {
      setReactingFor(replyId, true);
      await api.post('/posts/comments/react-reply', {
        postId,
        commentId,
        replyId,
        username: currentUser,
        action: desiredAction,
      });
      await refreshInteractions();
    } catch (err) {
      console.error('❌ reactToReply failed:', err);
      await refreshInteractions();
    } finally {
      setReactingFor(replyId, false);
    }
  };

  const replyPrefix = replyingTo?.username ? `@${replyingTo.username} ` : '';

  useEffect(() => {
    if (!setNewComment) return;
    if (replyPrefix) {
      setNewComment((prev = '') => withPrefix(prev || '', replyPrefix));
    }
  }, [replyPrefix, setNewComment]);

  const handleQuickEmoji = (emoji) => {
    if (!setNewComment) return;

    setNewComment((prev = '') => {
      const current = String(prev || '');

      if (replyingTo?.username) {
        const prefix = `@${replyingTo.username} `;
        const safe = withPrefix(current, prefix);
        const body = safe.slice(prefix.length);

        if (!body.trim()) return `${prefix}${emoji}`;
        return `${safe} ${emoji}`;
      }

      if (!current.trim()) return emoji;
      return `${current} ${emoji}`;
    });
  };



const resizeCommentTextarea = () => {
  const textarea = commentTextareaRef.current;

  if (!textarea) {
    return;
  }

  textarea.style.height = '42px';

  const nextHeight = Math.min(
    textarea.scrollHeight,
    132
  );

  textarea.style.height = `${nextHeight}px`;
  textarea.style.overflowY =
    textarea.scrollHeight > 132
      ? 'auto'
      : 'hidden';
};

useEffect(() => {
  resizeCommentTextarea();
}, [newComment]);


const handlePostAndRefresh = async () => {
  if (commentBlocked || checkingPrivacy) return;

  try {
    setPosting(true);

      if (replyingTo?.commentId) {
        await api.post(`/posts/${postId}/reply-comment`, {
          postId,
          commentId: replyingTo.commentId,
          parentReplyId: replyingTo.replyId || null,
          username: currentUser,
          text: newComment,
        });

        const parent =
          enrichedComments.find((c) => c.id === replyingTo.commentId) ||
          comments.find?.((c) => (c.id || c._id || c.commentId) === replyingTo.commentId);

        const commentOwner = parent?.username;

        if (commentOwner && commentOwner !== currentUser) {
          try {
            await api.post('/notify', {
              type: 'reply_comment',
              fromUser: currentUser,
              toUser: commentOwner,
              postId,
              text: `${currentUser} replied to your comment`,
            });
          } catch (err) {
            console.error('❌ Failed to send reply_comment notification:', err);
          }
        }

        pendingScrollRef.current = { type: 'comment', commentId: replyingTo.commentId };
        setReplyingTo(null);
      } else if (handleComment) {
        const created = handleComment();
        await (created?.then ? created : Promise.resolve(created));
        pendingScrollRef.current = { type: 'top' };
      }

setNewComment?.('');

requestAnimationFrame(() => {
  if (commentTextareaRef.current) {
    commentTextareaRef.current.style.height = '42px';
    commentTextareaRef.current.style.overflowY = 'hidden';
  }
});

await refreshInteractions();

    } catch (err) {
      console.error('❌ Post failed:', err);
    } finally {
      setPosting(false);
    }
  };


return createPortal(
  <div
    className="comment-drawer-backdrop"
    onClick={handleBackdropClick}
  >

      <div
        className="comment-drawer-content"
        onClick={stopDrawerPropagation}
        onMouseDown={stopDrawerPropagation}
        onTouchStart={stopDrawerPropagation}
      >
        <div className="comment-header">
          <span>Comments{total ? ` • ${total}` : ''}</span>
          <button className="comment-close" onClick={() => safeClose({ manual: true })}>✕</button>
        </div>

        <div className="comment-scroll" ref={scrollRef}>
          {initialLoad ? (
            <CommentDrawerSkeleton count={3} />
          ) : enrichedComments.length === 0 ? (
            <p className="no-comments">{loadError || 'No comments yet.'}</p>
          ) : (
            enrichedComments.map((c, i) => {
              const isReacting = !!reacting[c.id];
              const likeActive = c.userReaction === 'like';
              const dislikeActive = c.userReaction === 'dislike';
              const likeIconSrc = likeActive ? commentLikeActiveIcon : commentLikeIcon;
              const dislikeIconSrc = dislikeActive ? commentDislikeActiveIcon : commentDislikeIcon;
              const replyCount = countRepliesDeep(c.replies);
              const isOpen = !!expanded[c.id];

const isMainReplyTarget =
  Boolean(replyingTo?.commentId) &&
  !replyingTo?.replyId &&
  String(replyingTo.commentId) ===
    String(c.id);

              return (


<div
  key={`${c.id || c._id || i}:${c.createdAt || ''}`}
  className={`comment-entry ${
    isMainReplyTarget
      ? 'comment-entry--reply-target'
      : ''
  }`}
  data-comment-drawer-target-id={c.id}


                  ref={(el) => {
                    if (el) commentRefs.current.set(c.id, el);
                    else commentRefs.current.delete(c.id);
                  }}
                >
                  <div className="comment-row">
                    <div
                      className="comment-left"
                      onMouseDown={startLongPressComment(c)}
                      onMouseUp={cancelLongPress}
                      onMouseLeave={cancelLongPress}
                      onTouchStart={startLongPressComment(c)}
                      onTouchEnd={cancelLongPress}
                    >
                      <img
                        src={c.profilePic || placeholderProfile}
                        alt="avatar"
                        className="comment-avatar"
                      />

                      <div className="comment-bubble">
                        <div className="comment-meta">
                          <Link to={`/user/${c.username}`} className="comment-link">
                            <span className="comment-name-row">
                              <span>@{c.username}</span>
                              {isSpecialUser(c) && (
                                <img
                                  src={goldTickIcon}
                                  alt={isAdminUser(c) ? 'Admin verified' : 'App verified'}
                                  className="comment-gold-tick"
                                  title={getSpecialTitle(c)}
                                />
                              )}
                            </span>
                          </Link>
                          <span className="dot-sep" aria-hidden="true">•</span>
                          <span className="timestamp-inline">
                            {getTimeAgo(c.createdAt || c.timestamp)}
                          </span>
                        </div>

                        <p className="comment-text">
                          <ReadMoreText text={c.text} maxLines={3} inline />
                        </p>

                        {replyCount > 0 && (
                          <button
                            type="button"
                            className="reply-toggle-btn"
                            onClick={() => toggleExpanded(c.id)}
                          >
                            {isOpen ? 'Hide replies' : `View ${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
                          </button>
                        )}

                        <div className="comment-actions-row">


<button
  className="reply-btn"
  disabled={commentBlocked || checkingPrivacy}
  onClick={() =>
    startReply({
      commentId: c.id,
      username: c.username,
    })
  }
>
  Reply
</button>


                          <div className="comment-actions-right">
                            <button
                              className={`comment-action-btn ${likeActive ? 'is-active' : ''}`}
                              aria-label={likeActive ? 'Unlike comment' : 'Like comment'}
                              disabled={isReacting || !currentUser || !postId}
                              onClick={() => reactToComment(c.id, 'like')}
                            >
                              <img
                                src={likeIconSrc}
                                alt="like"
                                className="comment-action-icon"
                                draggable="false"
                              />
                            </button>
                            <span className="comment-action-count">{c.likesCount ?? 0}</span>

                            <button
                              className={`comment-action-btn ${dislikeActive ? 'is-active' : ''}`}
                              aria-label={dislikeActive ? 'Remove dislike' : 'Dislike comment'}
                              disabled={isReacting || !currentUser || !postId}
                              onClick={() => reactToComment(c.id, 'dislike')}
                            >
                              <img
                                src={dislikeIconSrc}
                                alt="dislike"
                                className="comment-action-icon"
                                draggable="false"
                              />
                            </button>
                            <span className="comment-action-count">{c.dislikesCount ?? 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isOpen && c.replies?.length > 0 && (
                    <div className="replies">
                      {c.replies.map((r) => (



<ReplyThread
  key={r.id}
  reply={r}
  parentCommentId={c.id}
  postId={postId}
  currentUser={currentUser}
  replyingTo={replyingTo}
  setReplyingTo={setReplyingTo}
  startReply={startReply}
  reactToReply={reactToReply}
  reacting={reacting}
  startLongPressReply={startLongPressReply}
  cancelLongPress={cancelLongPress}
  commentBlocked={commentBlocked}
  checkingPrivacy={checkingPrivacy}
/>



                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {!initialLoad && !exhausted && (
            <div ref={loadMoreRef} className="comment-loading-sentinel">
              {loadingMore && <div className="comment-loading-spinner" aria-label="Loading more" />}
            </div>
          )}

          <div ref={bottomSentinelRef} />
        </div>

        <div className="comment-footer">
          {replyingTo && (
            <div className="replying-pill" role="status" aria-live="polite">
              <div className="pill-left">
                Replying to{' '}
                <strong>
                  <Link
                    to={`/user/${replyingTo.username || ''}`}
                    className="comment-link"
                  >
                    @{replyingTo.username || 'unknown'}
                  </Link>
                </strong>
              </div>

              <button
                className="pill-cancel-btn"
                aria-label="Cancel reply"
                onClick={() => {
                  setReplyingTo(null);
                  setNewComment && setNewComment('');
                }}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          )}

          <div className="comment-quick-emojis" aria-label="Quick emojis">
            {QUICK_COMMENT_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="comment-quick-emoji-btn"
                onClick={() => handleQuickEmoji(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>

  


<div
  className={`comment-composer-pill ${
    commentBlocked
      ? 'comment-composer-pill--blocked'
      : ''
  }`}
>

<img
  src={currentUserProfilePic || placeholderProfile}
  alt=""
  className="comment-composer-avatar"
  onError={(event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = placeholderProfile;
  }}
/>

  <div className="comment-composer-input-wrap">
    <textarea
      ref={commentTextareaRef}
      rows={1}
      className="comment-textarea"
      placeholder={
        commentBlocked
          ? 'You cannot comment on this post.'
          : replyingTo?.commentId
          ? 'Write a reply...'
          : 'Write a comment...'
      }
      value={newComment}
      disabled={
        commentBlocked ||
        checkingPrivacy
      }
      onChange={(event) => {
        const value =
          event.target.value ?? '';

        if (replyingTo?.username) {
          const prefix =
            `@${replyingTo.username} `;

          const nextValue =
            withPrefix(
              value,
              prefix
            );

          setNewComment(nextValue);
        } else {
          setNewComment(value);
        }

        requestAnimationFrame(
          resizeCommentTextarea
        );
      }}
      onKeyDown={(event) => {
        if (!replyingTo?.username) {
          return;
        }

        const prefix =
          `@${replyingTo.username} `;

        const element =
          event.target;

        const start =
          element.selectionStart ?? 0;

        const end =
          element.selectionEnd ?? 0;

        const hittingBackspace =
          event.key ===
            'Backspace' &&
          start <= prefix.length &&
          end <= prefix.length;

        const hittingDelete =
          event.key ===
            'Delete' &&
          start < prefix.length &&
          end <= prefix.length;

        if (
          hittingBackspace ||
          hittingDelete
        ) {
          event.preventDefault();

          requestAnimationFrame(() => {
            try {
              element.setSelectionRange(
                prefix.length,
                prefix.length
              );
            } catch {}
          });
        }
      }}
      onClick={(event) => {
        if (!replyingTo?.username) {
          return;
        }

        const prefix =
          `@${replyingTo.username} `;

        const element =
          event.target;

        const position =
          element.selectionStart ?? 0;

        if (
          position <
          prefix.length
        ) {
          requestAnimationFrame(() => {
            try {
              element.setSelectionRange(
                prefix.length,
                prefix.length
              );
            } catch {}
          });
        }
      }}
    />
  </div>

  <button
    type="button"
    className="comment-send-icon-btn"
    onClick={
      handlePostAndRefresh
    }
    disabled={
      posting ||
      checkingPrivacy ||
      commentBlocked ||
      !String(
        newComment || ''
      ).trim()
    }
    aria-label={
      commentBlocked
        ? 'Commenting blocked'
        : checkingPrivacy
        ? 'Checking comment permissions'
        : posting
        ? 'Posting comment'
        : replyingTo?.commentId
        ? 'Post reply'
        : 'Post comment'
    }
  >
    {posting ||
    checkingPrivacy ? (
      <span
        className="comment-send-spinner"
        aria-hidden="true"
      />
    ) : (
      <img
        src={sendIcon}
        alt=""
        className="comment-send-icon"
      />
    )}
  </button>
</div>





        </div>

        {refreshing && (
          <div className="comments-refresh-overlay" aria-hidden="true">
            <div className="comments-spinner" />
          </div>
        )}
      </div>

      <DeleteCommentDrawer
        isOpen={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
        onOptimistic={() => {
          if (!deleteTarget) return;
          snapshotRef.current = enrichedComments;
          optimisticRef.current = true;
          const { type, commentId, replyId, replyMeta } = deleteTarget;

          if (type === 'comment') {
            deletedIdsRef.current.add(commentId);
            setEnrichedComments((prev) => prev.filter((c) => c.id !== commentId));
          } else {
            setEnrichedComments((prev) =>
              prev.map((c) => {
                if (c.id !== commentId) return c;
                const filtered = (c.replies || []).filter((r) => {
                  if (replyId) return r.id !== replyId;
                  const tsMatch = replyMeta?.timestamp
                    ? (r.createdAt || r.timestamp) === replyMeta.timestamp
                    : true;
                  const textMatch = replyMeta?.text ? r.text === replyMeta.text : true;
                  return !(tsMatch && textMatch);
                });
                return { ...c, replies: filtered };
              })
            );
          }
        }}
        onRollback={() => {
          if (snapshotRef.current) {
            setEnrichedComments(snapshotRef.current);
          }
          optimisticRef.current = false;
        }}
        onConfirm={async () => {
          await doDelete();
          await refreshInteractions();
        }}
        type={deleteTarget?.type || 'comment'}
        authorUsername={deleteTarget?.authorUsername || ''}
        textSnippet={deleteTarget?.textSnippet || ''}
        createdAt={deleteTarget?.createdAt || ''}
      />


<BlockConfirmDrawer
  isOpen={blockOpen}
  mode={
    isUsernameBlockedLocally(blockTarget?.targetUsername)
      ? 'unblock'
      : 'block'
  }
  targetUsername={blockTarget?.targetUsername || ''}
  loading={blockActionLoading}
  onClose={() => {
    if (!blockActionLoading) {
      setBlockOpen(false);
      setBlockTarget(null);
    }
  }}
  onConfirm={doToggleBlockUser}
/>

{commentToast.visible && (
  <div className={`comment-drawer-toast ${commentToast.type}`}>
    {commentToast.message}
  </div>
)}

    </div>,
    document.body
  );
}
